import styled from 'styled-components';
import { breakpoints } from 'theme';

export const StyledPreviewFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px;

  @media ${breakpoints.downSmPlus} {
    padding: 12px;
  }
`;

export const StyledPreview = styled.div`
  display: flex;
  flex-direction: column;
  width: min(680px, 100%);
  max-height: calc(100svh - 48px);
  text-align: left;
  font-weight: 400;
  padding: 8px;
  border-radius: 22px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.blackSqueeze};
  box-shadow: 0 24px 48px -28px ${({ theme }) => `${theme.color.pickledBluewood}59`};

  @media ${breakpoints.downSmPlus} {
    max-height: calc(100svh - 24px);
    padding: 6px;
    border-radius: 18px;
  }
`;

export const StyledPreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;
`;

export const StyledPreviewHeading = styled.div`
  min-width: 0;
`;

export const StyledPreviewTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const StyledPreviewCount = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledPreviewSheet = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 28px 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.white};

  @media ${breakpoints.downSmPlus} {
    padding: 4px 16px 16px;
    border-radius: 14px;
  }
`;

export const StyledMetaRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const StyledMetaLabel = styled.span`
  flex-shrink: 0;
  width: 48px;
  padding-top: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledMetaValue = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledFromName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.shark};
`;

export const StyledFromEmail = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.color.baliHai};
  overflow-wrap: anywhere;
`;

export const StyledRecipientList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const StyledRecipientChip = styled.span`
  position: relative;
  max-width: 220px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  font-size: 13px;
  color: ${({ theme }) => theme.color.shark};
  cursor: default;

  &:hover,
  &:focus-visible {
    z-index: 2;
    outline: none;
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const StyledRecipientChipLabel = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledRecipientTip = styled.span`
  position: absolute;
  z-index: 3;
  top: calc(100% + 6px);
  left: 0;
  max-width: 280px;
  padding: 6px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.shark};
  color: ${({ theme }) => theme.color.white};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;

  ${StyledRecipientChip}:hover &,
  ${StyledRecipientChip}:focus-visible & {
    opacity: 1;
    visibility: visible;
  }
`;

export const StyledRecipientDirectory = styled.div`
  width: 100%;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 12px;
`;

export const StyledRecipientRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 12px;

  &:hover {
    background: ${({ theme }) => theme.color.blackSqueeze};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  }

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`;

export const StyledRecipientName = styled.span`
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.shark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledRecipientEmail = styled.span`
  flex-shrink: 1;
  min-width: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;

  @media ${breakpoints.downSmPlus} {
    text-align: left;
  }
`;

export const StyledMoreButton = styled.button`
  align-self: flex-start;
  width: fit-content;
  height: 28px;
  padding: 0 12px;
  border: 1px dashed ${({ theme }) => theme.color.botticelli};
  border-radius: 999px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

export const StyledSubject = styled.h3`
  margin: 24px 0 32px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.color.shark};

  @media ${breakpoints.downSmPlus} {
    font-size: 18px;
  }
`;

export const StyledBody = styled.div`
  font-size: 15px;
  font-weight: 400;
  line-height: 1.65;
  color: ${({ theme }) => theme.color.pickledBluewood};
  overflow-wrap: anywhere;

  a {
    color: ${({ theme }) => theme.color.brightTurquoise};
    text-decoration: underline;
  }

  table a,
  table a font {
    color: ${({ theme }) => theme.color.black};
    text-decoration: none;
  }

  @media ${breakpoints.downSmPlus} {
    font-size: 14px;
  }
`;

export const StyledAttachmentList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 32px 0 16px;
  padding: 0;
  list-style: none;
`;

export const StyledAttachmentItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.aquaSqueeze};
`;

export const StyledAttachmentName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.shark};
`;

export const StyledAttachmentSize = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledPreviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 8px 4px;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

export const StyledBackButton = styled.button`
  height: 44px;
  padding: 0 18px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 12px;
  background: ${({ theme }) => theme.color.white};
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.shark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StyledConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 120px;
  height: 44px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.black};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.white};
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.brightTurquoise};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
